import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { CenterFlex, Flex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import {
  getPersonalPreferenceTabKeys,
  SETTING_TAB_KEYS,
  type SettingSidebarMenuT,
  type TabItemT,
  useSettingTabItems,
} from '@/entities/settings';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useWorkspaceStore from '@/stores/WorkspaceStore';

import packageJson from '../../../../package.json';

const VERSION = `${packageJson.version}v`;

export type SettingTabT = {
  tabItem: TabItemT;
  selected: boolean;
  onClick: () => void;
};

const SettingTab = ({ tabItem, selected, onClick }: SettingTabT) => {
  const color = useColor();
  const { Icon, text, isBeta } = tabItem;

  return (
    <TabItemWrapper
      a="center"
      d="row"
      g="8px"
      onClick={onClick}
      isSelected={selected}
    >
      <CenterFlex style={{ width: 16, height: 16, fontSize: 16 }}>
        {Icon}
      </CenterFlex>
      <Typo type="body3" weight="m" color={color.gray700}>
        {text}
      </Typo>
      {isBeta && <BetaLabel>beta</BetaLabel>}
    </TabItemWrapper>
  );
};

const SettingSidebar = () => {
  const settingTabItems = useSettingTabItems();
  const color = useColor();
  const location = useLocation();
  const wr = useWorkspaceRouter();
  const { currWorkspace } = useWorkspaceStore((state) => ({
    currWorkspace: state.currWorkspace,
  }));
  const personalPreferenceTabKeys = getPersonalPreferenceTabKeys();
  const visibleTabKeys = (tabKeys: readonly SettingSidebarMenuT[]) =>
    tabKeys.filter((key) => {
      const item = settingTabItems[key];
      if (!currWorkspace?.alias) return false;
      if (item.hidden && item.hidden(currWorkspace.alias)) return false;
      return true;
    });
  const visibleSettingTabKeys = visibleTabKeys(SETTING_TAB_KEYS);
  const visiblePersonalPreferenceTabKeys = visibleTabKeys(
    personalPreferenceTabKeys,
  );
  const onTabClick = (key: SettingSidebarMenuT) => {
    if (!currWorkspace) return;
    wr.push(`/settings/${key}`);
  };

  return (
    <>
      <Flex height="56px" a="center">
        <Typo
          type="label2"
          weight="sb"
          color={color.gray700}
          style={{ marginLeft: '24px' }}
        >
          Settings
        </Typo>
      </Flex>
      <Flex d="column" g="8px" style={{ marginLeft: '12px' }}>
        {visibleSettingTabKeys.map((key) => (
          <SettingTab
            key={key}
            tabItem={settingTabItems[key]}
            selected={location.pathname.includes(key)}
            onClick={() => onTabClick(key)}
          />
        ))}
      </Flex>
      {visiblePersonalPreferenceTabKeys.length > 0 && (
        <PersonalPreferenceSection>
          <Typo
            type="label3"
            weight="m"
            color={color.gray500}
            style={{ marginLeft: '12px' }}
          >
            Personal preference
          </Typo>
          <Flex d="column" g="8px" style={{ marginLeft: '12px' }}>
            {visiblePersonalPreferenceTabKeys.map((key) => (
            <SettingTab
              key={key}
              tabItem={settingTabItems[key]}
              selected={location.pathname.includes(key)}
              onClick={() => onTabClick(key)}
            />
            ))}
          </Flex>
        </PersonalPreferenceSection>
      )}
      <Typo
        type="label3"
        color={color.ND40}
        style={{ position: 'absolute', left: 16, bottom: 8 }}
      >
        Syncly {VERSION}
      </Typo>
    </>
  );
};

export default SettingSidebar;

const PersonalPreferenceSection = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const TabItemWrapper = styled(Flex)<{ isSelected?: boolean }>`
  width: 196px;
  height: 28px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${({ theme }) => theme.colors.gray700};
  background-color: ${({ theme }) => theme.colors.gray100};
  :hover {
    background-color: ${({ theme }) => theme.colors.hover};
  }
  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.colors.selected};
      :hover {
        background-color: ${theme.colors.selected_hover};
      }
    `};
`;

const BetaLabel = styled.div`
  background-color: ${(props) => props.theme.colors.primaryAlpha(0.1)};
  color: ${(props) => props.theme.colors.primary100};
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
`;
